import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import DataTypeFormatter from 'common/DataTypeFormatter';
import { I18n } from 'common/i18n';
import { type ViewColumn } from 'common/types/viewColumn';

import { NULL_GROUPING } from '../Constants';
import { type TableColumnFormat } from '../types';

/**
 * Render types whose group/pivot label should be run through the column formatter. Other types
 * (url, email, html, blob, etc.) would produce anchor tags from renderCellHTML, so they fall back
 * to String(key) — keeping raw HTML out of the label.
 */
const FORMATTED_GROUP_RENDER_TYPES = new Set([
  'number',
  'money',
  'percent',
  'stars',
  'calendar_date',
  'date',
]);

export interface FormatGroupLabelValueArgs {
  key: unknown;
  fieldName: string | null | undefined;
  viewColumns: ViewColumn[];
  columnFormats: { [key: string]: TableColumnFormat };
  domain: string;
  datasetUid: string;
}

/**
 * Formats a grouped/pivoted key for display using its source column's format.
 *
 * A group or pivot label is otherwise the raw key (e.g. a floating timestamp
 * `2023-01-15T00:00:00.000`), which ignores any column/conditional format the user configured.
 * Running the key through the column's formatter — with any `columnFormats` override merged in —
 * makes the label match the cells (e.g. `01/15/2023`, `Subtotal 2,012`). Shared by the pivot
 * column headers and the table export subtotal labels.
 */
export const formatGroupLabelValue = ({
  key,
  fieldName,
  viewColumns,
  columnFormats,
  domain,
  datasetUid,
}: FormatGroupLabelValueArgs): string => {
  if (key === NULL_GROUPING || isNil(key)) {
    return I18n.t('shared.visualizations.charts.common.summary_table.no_value');
  }
  const viewColumn = fieldName ? viewColumns.find((column) => column.fieldName === fieldName) : undefined;
  if (!viewColumn || !FORMATTED_GROUP_RENDER_TYPES.has(viewColumn.renderTypeName ?? '')) {
    return String(key);
  }
  const overrideFormat = columnFormats?.[fieldName ?? '']?.format;
  const metadata = isEmpty(overrideFormat)
    ? viewColumn
    : { ...viewColumn, format: { ...(viewColumn.format ?? {}), ...overrideFormat } };
  return DataTypeFormatter.renderCellHTML(key, metadata, domain, datasetUid);
};
